// ft3d-hud/forms-window.ts — the Forms window, shared by every FT3D route.
//
// It lives in the HUD rather than in my_campaign.php's window kit because item.php and
// ft3d_scene_host.php have no FT3D action bar to hang a window from, and once the legacy drawer is
// suppressed (side-panels-init.ts) this is the ONLY way into the Forms host on those routes.
//
// ⛔ The canonical host #div_refresh_sidelog_form is MOVED here, never cloned — 19 files target that
// id and must keep resolving it.

const FORMS_OPEN_KEY = 'ft3d.hud.forms';

/** Status forms declare their identity; the shared window owns only their FT3D presentation. */
function observeStatusFormHeader(host: HTMLElement, panel: HTMLElement): void {
    const title = panel.querySelector<HTMLElement>('.ft3d-hud__window-bar strong');
    if (!title) return;
    let current: HTMLElement | null = null;
    const sync = (): void => {
        const head = host.querySelector<HTMLElement>('[data-ft-status-form-identity]');
        if (head === current) return;
        current = head;
        const formTitle = head?.querySelector<HTMLElement>('[data-ft-status-form-title]');
        title.textContent = formTitle?.textContent || 'Forms';
        panel.setAttribute('aria-label', title.textContent);
        if (!head || !formTitle) return;
        formTitle.hidden = true;
        const name = head.querySelector<HTMLElement>('[data-ft-status-form-name]');
        if (!name) return;
        head.style.cssText = 'display:flex;align-items:center;gap:12px;padding:0 0 8px;text-align:left';
        name.style.cssText = 'min-width:0;overflow-wrap:anywhere';
        const id = head.dataset.ftStatusEntityId || '';
        const inventory = head.dataset.ftStatusFormIdentity === 'inventory';
        const cacheWindow = window as Window & {
            ftResolveCachedItemForRender?: (id: string) => unknown;
            FateTide?: { casterCache?: { get: (id: string) => unknown } };
        };
        const entity = inventory
            ? cacheWindow.ftResolveCachedItemForRender?.(id)
            : cacheWindow.FateTide?.casterCache?.get(id);
        const portrait = document.createElement('span');
        portrait.dataset.ftStatusFormPortrait = '';
        portrait.style.cssText = 'width:56px;height:56px;flex:0 0 56px;display:flex;align-items:center;justify-content:center;overflow:hidden;border-radius:4px;background:rgba(255,255,255,.06)';
        let media: HTMLImageElement | HTMLVideoElement | null = null;
        if (entity && inventory && typeof renderInventoryThumbnail === 'function') {
            const template = document.createElement('template');
            template.innerHTML = renderInventoryThumbnail(entity, {}, { show_overlay: false, show_icons: false, omit_tooltip: true });
            media = template.content.querySelector('img, video');
        } else if (entity && typeof selectCreatureMedia === 'function') {
            const selected = selectCreatureMedia(entity, { use_picture_thumbnail: true });
            if (selected?.url) {
                media = document.createElement(selected.type === 'video' ? 'video' : 'img');
                media.src = selected.url;
            }
        }
        if (media) {
            media.removeAttribute('id');
            media.className = '';
            media.style.cssText = 'width:100%;height:100%;object-fit:contain';
            if (media instanceof HTMLImageElement) media.alt = '';
            else { media.muted = true; media.loop = true; media.autoplay = true; media.playsInline = true; }
            portrait.appendChild(media);
        } else {
            portrait.textContent = name.textContent?.trim().slice(0, 2).toUpperCase() || '?';
        }
        head.insertBefore(portrait, name);
    };
    new MutationObserver(sync).observe(host, { childList: true, subtree: true });
    sync();
}

interface Ft3dFormsReceipt {
    schema: 'ft.ft3d.forms-entry.v3';
    canonicalHostId: 'div_refresh_sidelog_form';
    host: 'ft3d_hud_forms_window';
    portalIsQueryOnly: true;
    portalWrites: 0;
    hostMoved: boolean;
}

let windowEl: HTMLElement | null = null;
let toggleEl: HTMLElement | null = null;
let mounted = false;

function readOpen(): boolean {
    try { return window.localStorage.getItem(FORMS_OPEN_KEY) === 'on'; } catch { return false; }
}

function writeOpen(open: boolean): void {
    try { window.localStorage.setItem(FORMS_OPEN_KEY, open ? 'on' : 'off'); } catch { /* viewer-local nicety only */ }
}

function apply(open: boolean): void {
    if (windowEl) windowEl.hidden = !open;
    // EVERY control that points at this window, not just the dock button — my_campaign.php's bezel
    // Forms button declares aria-controls too and would otherwise report "collapsed" forever.
    document.querySelectorAll<HTMLElement>('[aria-controls="ft3d_hud_forms_window"]').forEach((control) => {
        control.setAttribute('aria-expanded', open ? 'true' : 'false');
        control.classList.toggle('is-active', open);
    });
    if (!toggleEl) return;
    const label = open ? 'Hide the forms window' : 'Show the forms window';
    toggleEl.setAttribute('aria-label', label);
    toggleEl.setAttribute('title', label);
}

export function isFormsWindowOpen(): boolean {
    if (!windowEl) return readOpen();
    return !windowEl.hidden;
}

export function setFormsWindowOpen(open: boolean): void {
    writeOpen(open);
    apply(open);
}

function renderGlyph(): void {
    if (!toggleEl) return;
    const glyph = toggleEl.querySelector<HTMLElement>('[data-ft3d-button-glyph]');
    const iconMapFn = (window as unknown as { iconMap?: (k: string, w: number, h: number, t: string) => string }).iconMap;
    if (!glyph || typeof iconMapFn !== 'function') return;
    glyph.innerHTML = iconMapFn('view_forms', 22, 22, 'Forms');
}

export function mountFt3dFormsWindow(root: HTMLElement): void {
    if (mounted) return;
    windowEl = root.querySelector<HTMLElement>('[data-ft3d-forms-window]');
    toggleEl = root.querySelector<HTMLElement>('[data-ft3d-forms-toggle]');
    if (!windowEl) return;

    const host = document.getElementById('div_refresh_sidelog_form');
    const mount = windowEl.querySelector<HTMLElement>('[data-ft3d-forms-mount]');
    let hostMoved = false;
    if (host && mount) {
        mount.appendChild(host);
        observeStatusFormHeader(host, windowEl);
        hostMoved = true;
    }

    const receipt: Ft3dFormsReceipt = {
        schema: 'ft.ft3d.forms-entry.v3',
        canonicalHostId: 'div_refresh_sidelog_form',
        host: 'ft3d_hud_forms_window',
        portalIsQueryOnly: true,
        portalWrites: 0,
        hostMoved,
    };
    (window as unknown as { __ft3dFormsEntry?: Ft3dFormsReceipt }).__ft3dFormsEntry = receipt;

    if (!hostMoved) {
        // Without the canonical host the window can only ever show an empty box. Hide the control
        // rather than offer one that opens nothing — the receipt above records why.
        if (toggleEl) {
            toggleEl.hidden = true;
            toggleEl.setAttribute('aria-disabled', 'true');
        }
        mounted = true;
        return;
    }

    if (toggleEl) {
        toggleEl.addEventListener('click', () => setFormsWindowOpen(!!windowEl?.hidden));
    }
    root.querySelectorAll<HTMLElement>('[data-ft3d-forms-close]').forEach((button) => {
        button.addEventListener('click', () => setFormsWindowOpen(false));
    });

    // The route's own chrome (my_campaign.php's bezel Forms button) opens this through the global
    // rather than reaching into the module — one window, one owner, whoever asks.
    (window as unknown as { ftOpenFt3dForms?: () => void }).ftOpenFt3dForms = () => setFormsWindowOpen(true);
    // ⚠ OPEN and TOGGLE are different asks and a control gets the wrong one by accident. The rim
    // Forms tab must be able to close what it opened — wired to ftOpenFt3dForms it opened the window
    // and then did nothing on every later press (owner, 2026-08-21). Callers that genuinely mean
    // "open" (the Workspace's "Open Forms" button) keep ftOpenFt3dForms. Which one a control gets is
    // declared by data-ft3d-open-forms="toggle" in the markup, never inferred from its ARIA.
    // (NOT data-ft3d-forms-toggle — that attribute is this module's own dock button, queried above.)
    (window as unknown as { ftToggleFt3dForms?: () => void }).ftToggleFt3dForms =
        () => setFormsWindowOpen(!isFormsWindowOpen());
    // side-panels-init.ts's isSideLogFormPaneActive() reads this so its narrow-viewport "don't open
    // the info panel over an open form" guard still fires on an FT3D route, where the legacy pane it
    // used to inspect is permanently inactive.
    (window as unknown as { ftFt3dFormsWindowOpen?: () => boolean }).ftFt3dFormsWindowOpen = isFormsWindowOpen;

    // ⛔ COMPATIBILITY BRIDGE — do not remove. ~15 source files (player notes, character
    // description, summary, status effects, inventory actions, the creature console …) show a form
    // by clicking #pills_sidelog_form_tab after opening the drawer. With the drawer gated, that
    // click would activate an empty legacy pane and the user would see nothing. Delegated, because
    // several of those callers fire before this module mounts.
    document.addEventListener('click', (event) => {
        const target = event.target instanceof Element
            ? event.target.closest('#pills_sidelog_form_tab')
            : null;
        if (target) setFormsWindowOpen(true);
    });
    // ⚠ Two callers use `dispatchEvent(new Event('click'))`, and a bare Event does NOT bubble — so
    // the delegated listener above never sees them. Bind the element directly as well; opening an
    // already-open window is a no-op, so the overlap is harmless.
    document.getElementById('pills_sidelog_form_tab')
        ?.addEventListener('click', () => setFormsWindowOpen(true));

    mounted = true;
    apply(readOpen());
    renderGlyph();
    window.setTimeout(renderGlyph, 0);
    window.addEventListener('load', renderGlyph, { once: true });
}
